import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Bell, Save, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

interface WorkingHours {
  start: string;
  end: string;
  enabled: boolean;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { settings, updateSettings } = useSettings();
  const [localSettings, setLocalSettings] = useState(settings);

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleWorkingHoursChange = (day: keyof typeof localSettings.workingHours, field: keyof WorkingHours, value: string | boolean) => {
    setLocalSettings(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value
        }
      }
    }));
  };

  const handleNotificationChange = (field: keyof typeof localSettings.notifications, value: boolean | number) => {
    setLocalSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      updateSettings(localSettings);
      setSaveMessage('Configuración guardada exitosamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error al guardar la configuración');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const dayNames = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  // Only allow access to admin users
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900">Acceso Denegado</h2>
          <p className="text-gray-600">Solo los administradores pueden acceder a esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h1>
          <p className="text-gray-600">Administra las configuraciones del calendario y notificaciones</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Working Hours Section */}
          <div>
            <div className="flex items-center mb-4">
              <Clock className="h-5 w-5 text-blue-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Horario de Trabajo</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-4">Configura las horas laborables para cada día de la semana</p>
              <div className="space-y-3">
                {Object.entries(localSettings.workingHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-4 p-3 bg-white rounded border">
                    <div className="w-20">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={hours.enabled}
                          onChange={(e) => handleWorkingHoursChange(day as keyof typeof localSettings.workingHours, 'enabled', e.target.checked)}
                          className="mr-2 h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="text-sm font-medium">{dayNames[day as keyof typeof dayNames]}</span>
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Desde:</label>
                      <input
                        type="time"
                        value={hours.start}
                        disabled={!hours.enabled}
                        onChange={(e) => handleWorkingHoursChange(day as keyof typeof localSettings.workingHours, 'start', e.target.value)}
                        className="px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Hasta:</label>
                      <input
                        type="time"
                        value={hours.end}
                        disabled={!hours.enabled}
                        onChange={(e) => handleWorkingHoursChange(day as keyof typeof localSettings.workingHours, 'end', e.target.value)}
                        className="px-2 py-1 border rounded text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar Settings Section */}
          <div>
            <div className="flex items-center mb-4">
              <Calendar className="h-5 w-5 text-green-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Configuración del Calendario</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vista predeterminada del calendario</label>
                  <select
                    value={localSettings.calendarView}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, calendarView: e.target.value as 'fullWeek' | 'workWeek' }))}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="workWeek">Semana laboral (Lun-Vie)</option>
                    <option value="fullWeek">Semana completa (Lun-Dom)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {localSettings.calendarView === 'workWeek' 
                      ? 'Solo muestra días laborables en la vista semanal' 
                      : 'Muestra todos los días de la semana incluyendo fines de semana'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div>
            <div className="flex items-center mb-4">
              <Bell className="h-5 w-5 text-yellow-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Configuración de Notificaciones</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localSettings.notifications.emailReminders}
                      onChange={(e) => handleNotificationChange('emailReminders', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">Recordatorios por email</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localSettings.notifications.smsReminders}
                      onChange={(e) => handleNotificationChange('smsReminders', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">Recordatorios por SMS</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localSettings.notifications.dailySchedule}
                      onChange={(e) => handleNotificationChange('dailySchedule', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">Agenda diaria por email</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localSettings.notifications.weeklySchedule}
                      onChange={(e) => handleNotificationChange('weeklySchedule', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">Resumen semanal</span>
                  </label>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiempo de recordatorio antes de la cita
                  </label>
                  <select
                    value={localSettings.notifications.reminderTime}
                    onChange={(e) => handleNotificationChange('reminderTime', parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={1}>1 hora antes</option>
                    <option value={2}>2 horas antes</option>
                    <option value={4}>4 horas antes</option>
                    <option value={12}>12 horas antes</option>
                    <option value={24}>24 horas antes</option>
                    <option value={48}>48 horas antes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`mt-4 p-3 rounded-md text-sm ${saveMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {saveMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;